import React, { useState } from 'react';
import { IMaskInput } from 'react-imask';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { convertTo12HourTime } from 'utils/date';
import InputStyles from './InputStyles';

interface ITimeInputProps {
  id?: string;
  type?: string;
  value?: string;
  width?: number;
  height?: number;
  maxLength?: number;
  className?: string;
  disabled?: boolean;
  onBlur?: (value: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: string) => void;
}

const WIDTH_ICON_CONTAINER = 35.75;

const Layout = styled.div<{ width?: number }>`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: ${({ width }) => (width ? toRem(width) : '100%')};
  height: auto;

  input {
    padding-right: ${toRem(WIDTH_ICON_CONTAINER)};
  }
`;

interface ToggleButtonProps {
  selected?: boolean;
}

const ToggleButton = styled.button<ToggleButtonProps>`
  margin-left: ${toRem(4)};
  font-size: ${toRem(10)};
  font-weight: ${theme.font.weight.semiBold};

  ${({ selected }) =>
    !selected &&
    css`
      color: ${theme.color.neutral[300]};

      &:hover,
      &:focus {
        color: ${theme.color.neutral[500]};
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: auto;
    `}
`;

const IconContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${toRem(WIDTH_ICON_CONTAINER)};
  height: 100%;
  right: ${toRem(16)};
`;

const StyledInput = styled(IMaskInput)<Partial<ITimeInputProps>>`
  ${InputStyles};

  font-size: ${toRem(14)};

  ${({ height }) =>
    height &&
    css`
      height: ${toRem(height)};
    `}

  ${({ width }) =>
    width &&
    css`
      width: ${toRem(width)};
    `}
`;

const getHours = (val?: string) => {
  const time = val?.split(':', 2);
  return Number(time?.[0] || 0);
};

const masks = [
  {
    mask: '1:23',
    definitions: {
      1: /[1-9]/,
      2: /[0-5]/,
      3: /[0-9]/,
    },
  },
  {
    mask: '12:34',
    definitions: {
      1: /0/,
      2: /[1-9]/,
      3: /[0-5]/,
      4: /[0-9]/,
    },
  },
  {
    mask: '12:34',
    definitions: {
      1: /1/,
      2: /[0-2]/,
      3: /[0-5]/,
      4: /[0-9]/,
    },
  },
];

const TimeMaskedInput: React.FC<ITimeInputProps> = ({ value, onBlur, onChange, width, disabled, ...rest }: ITimeInputProps) => {
  const [time, setTime] = useState(value && convertTo12HourTime(value));
  const [isPM, setIsPM] = useState(getHours(value) >= 12);

  const handleChange = (val: string, pm: boolean) => {
    if (onChange) {
      if (!val) {
        onChange(val);
      } else {
        const time = val.split(':', 2);
        const hours = Number(time[0]);
        const newHours = pm ? (hours >= 12 ? hours : hours + 12) : hours === 12 ? '00' : hours < 10 ? `0${hours}` : hours;

        onChange(`${newHours}:${time[1] || ''}`);
      }
    }
  };

  return (
    <Layout width={width}>
      <StyledInput
        placeholder="00:00"
        value={time}
        onBlur={onBlur}
        mask={masks}
        lazy={false}
        onAccept={(val: string) => {
          setTime(val);
          handleChange(val, isPM);
        }}
        disabled={disabled}
        readOnly={false}
        type="text"
        {...rest}
      />
      <IconContainer>
        <ToggleButton
          type="button"
          selected={!isPM}
          onClick={() => {
            setIsPM(false);
            handleChange(time, false);
          }}
          disabled={disabled}
        >
          AM
        </ToggleButton>
        <ToggleButton
          type="button"
          selected={isPM}
          onClick={() => {
            setIsPM(true);
            handleChange(time, true);
          }}
          disabled={disabled}
        >
          PM
        </ToggleButton>
      </IconContainer>
    </Layout>
  );
};

export default TimeMaskedInput;
